/**
 * Fonction pour gérer le changement de label DTMF sur un edge IVR
 */
export const createHandleEdgeLabelChange = (
  setEdges,
  setNodes,
  setCallFlowData,
  callFlowData,
  previousCallFlowDataRef,
  saveCallFlowToDatabase
) => {
  return (edgeId, newLabel) => {
    // Utiliser setNodes pour obtenir les nodes actuels
    let currentNodes = [];
    setNodes((ns) => {
      currentNodes = ns;
      return ns; // Retourner sans modification
    });
    
    // Utiliser setEdges avec une fonction pour accéder aux edges actuels
    setEdges((currentEdges) => {
      // Trouver l'edge concerné dans les edges actuels
      const edge = currentEdges.find(e => e.id === edgeId);
      if (!edge) {
        return currentEdges; // Retourner sans modification
      }

      // Extraire l'info du source (doit être un IVR routing)
      const sourceId = edge.source;
      if (!sourceId.startsWith('routing-')) {
        return currentEdges;
      }

      const routingKey = sourceId.replace('routing-', '');
      const oldLabel = edge.label;

      // Si le label n'a pas changé, ne rien faire
      if (oldLabel === newLabel) {
        return currentEdges;
      }

      // Trouver le targetNode pour construire l'option (utiliser currentNodes)
      const targetNode = currentNodes.find(n => n.id === edge.target);
      if (!targetNode) {
        return currentEdges;
      }

      // Construire l'option selon le type de target
      let option = null;
      
      // Si le target est un routing node
      if (edge.target.startsWith('routing-')) {
        const targetRoutingKey = edge.target.replace('routing-', '');
        const targetRouting = callFlowData?.routing?.[targetRoutingKey];
        
        if (!targetRouting) {
          return currentEdges;
        }
        
        // IMPORTANT: Pour tous les types de routing, on référence par routingName
        // Cela permet de retrouver le routing après un refresh
        const routingName = targetRouting.routingName || targetRoutingKey;
        
        option = {
          type: 'routing',
          routingName
        };
      }
      // Gérer les nœuds visuels (transfer-user-xxx, queue-xxx, disconnect-node)
      // Ces nœuds sont créés depuis les options IVR et correspondent à des routings
      else if (edge.target.startsWith('transfer-user-') || 
               edge.target.startsWith('transfer-pstn-') || 
               edge.target.startsWith('queue-') ||
               edge.target === 'disconnect-node') {
        
        // Chercher le routing correspondant dans callFlowData.routing
        let foundRoutingKey = null;
        let foundRouting = null;
        
        if (callFlowData?.routing) {
          Object.entries(callFlowData.routing).forEach(([key, routing]) => {
            // Pour transfer user
            if (edge.target.startsWith('transfer-user-')) {
              const userId = edge.target.replace('transfer-user-', '');
              if (routing.type === 'transfer' && routing.targetType === 'user' && routing.targetId === userId) {
                foundRoutingKey = key;
                foundRouting = routing;
              }
            }
            // Pour transfer phone
            else if (edge.target.startsWith('transfer-pstn-')) {
              const phoneId = edge.target.replace('transfer-pstn-', '');
              if (routing.type === 'transfer' && routing.targetType === 'phone' && routing.targetId === phoneId) {
                foundRoutingKey = key;
                foundRouting = routing;
              }
            }
            // Pour queue
            else if (edge.target.startsWith('queue-')) {
              const queueId = edge.target.replace('queue-', '');
              if (routing.type === 'queue' && routing.queueId === queueId) {
                foundRoutingKey = key;
                foundRouting = routing;
              }
            }
            // Pour disconnect
            else if (edge.target === 'disconnect-node') {
              if (routing.type === 'disconnect' && !foundRoutingKey) {
                foundRoutingKey = key;
                foundRouting = routing;
              }
            }
          });
        }
        
        if (foundRouting && foundRoutingKey) {
          const routingName = foundRouting.routingName || foundRoutingKey;
          
          option = {
            type: 'routing',
            routingName
          };
        }
      }

      if (!option) {
        return currentEdges;
      }

      // Créer une version mise à jour de callFlowData sans déclencher le useEffect de régénération
      const updatedCallFlowData = (() => {
        if (!callFlowData?.routing?.[routingKey]) {
          return callFlowData;
        }

        const routing = callFlowData.routing[routingKey];
        if (routing.type !== 'ivr' && routing.type !== 'IVR') {
          return callFlowData;
        }

        const options = { ...(routing.options || {}) };
        
        // Si l'ancien label existe et est différent du nouveau, supprimer l'ancienne entrée
        if (oldLabel && oldLabel !== newLabel && options[oldLabel]) {
          delete options[oldLabel];
        }
        
        // Ajouter la nouvelle entrée
        options[newLabel] = option;

        const updatedData = {
          ...callFlowData,
          routing: {
            ...callFlowData.routing,
            [routingKey]: {
              ...routing,
              options,
            },
          },
        };
        
        return updatedData;
      })();

      // Réinitialiser la référence pour forcer la régénération du diagramme
      previousCallFlowDataRef.current = null;
      
      // Mettre à jour callFlowData
      setCallFlowData(updatedCallFlowData);
      
      // Sauvegarder dans la base de données de manière asynchrone
      saveCallFlowToDatabase(updatedCallFlowData)
        .then((result) => {
          if (result) {
          } else {
            console.warn('⚠️ UpdateCallFlow returned empty result');
          }
        })
        .catch((error) => {
          console.error('❌ Erreur lors de la sauvegarde du DTMF:', error);
        });

      // Retourner les edges mis à jour avec le nouveau label
      return currentEdges.map((e) =>
        e.id === edgeId ? { ...e, label: newLabel } : e
      );
    });
  };
};
